import {MessageDescriptor} from '@lingui/core';
import {t} from '@lingui/macro';
import {useMemo} from 'react';

import notifyManagerImage from '../../../../assets/images/ruleLibrary/notifyManager.png';
import {CompanyFeaturesEnum} from '../../../../models/api/companyModel';
import {CustomFieldsEnum} from '../../../../models/api/customFieldModel';
import {RuleCompoundConditions} from '../../../../models/api/ruleModel';
import {SettingsCustomFieldsPagesEnum} from '../../../../models/url/settingsUrlModel';
import {RegexCharacterClassesEnum} from '../../../editors/commons/stringTemplateModel';
import {TransformerIdsEnum} from '../../../editors/expressions/expressionInferHelpers';
import {brandExpressionId, PreExpression} from '../../../editors/expressions/expressionModel';
import {RuleAction, RuleActionTypesEnum} from '../../ruleActionModel';
import {RuleConditionTypesEnum} from '../../ruleConditionModel';
import {RuleEventTypesEnum} from '../../ruleEventModel';
import {RuleIntegration} from '../../ruleIntegrationModel';
import {
  createCompoundRuleAndClause as and,
  createCompoundRuleOrClause as or,
  createRuleRecipeAction as action,
  createRuleRecipeCondition as condition,
  createRuleRecipeExpression as expression,
  LooseRuleRecipe,
  processRecipes,
  RuleRecipePlaceholder,
  RuleRecipePlaceholderTypesEnum,
} from './builder';

export enum RuleRecipeCategoriesEnum {
  CUSTOM = 'custom',
  ASSIGN = 'assign',
  MOVE = 'move',
  TAG = 'tag',
  ARCHIVE = 'archive',
  NOTIFY = 'notify',
  SLA = 'SLA',
  REPLY = 'reply',
  COMMENT = 'comment',
  // Used to promote smart rules during launch
  SMART_RULES = 'smart_rules',
  ACCOUNT = 'account',
}

const RECIPES: ReadonlyArray<LooseRuleRecipe> = [
  /** Move recipes */

  {
    id: 'move_sender',
    title: t('rule-library-recipe-move_sender-title')`Move based on sender`,
    description: t(
      'rule-library-recipe-move_sender-description',
    )`Move conversations sent from a person, address or number.`,
    categories: [RuleRecipeCategoriesEnum.MOVE],
    rule: {
      conditions: and(
        condition(RuleConditionTypesEnum.MESSAGE_FROM),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.MOVE)],
      events: [RuleEventTypesEnum.INBOUND],
    },
  },
  {
    id: 'move_recipient',
    title: t('rule-library-recipe-move_recipient-title')`Move based on recipient`,
    description: t(
      'rule-library-recipe-move_recipient-description',
    )`Move conversations sent to a person, address or number.`,
    categories: [RuleRecipeCategoriesEnum.MOVE],
    rule: {
      conditions: and(
        condition(RuleConditionTypesEnum.MESSAGE_TO),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.MOVE)],
      events: [RuleEventTypesEnum.INBOUND],
    },
  },
  {
    id: 'move_assignee',
    title: t('rule-library-recipe-move_assignee-title')`Move based on assignee`,
    description: t(
      'rule-library-recipe-move_assignee-description',
    )`Move conversations when assigned to a particular teammate.`,
    categories: [RuleRecipeCategoriesEnum.MOVE],
    rule: {
      conditions: and(
        condition(RuleConditionTypesEnum.ASSIGNEE_IS),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.MOVE)],
      events: [RuleEventTypesEnum.ASSIGN],
    },
  },
  {
    id: 'move_keyword',
    title: t('rule-library-recipe-move_keyword-title')`Move based on keyword`,
    description: t(
      'rule-library-recipe-move_keyword-description',
    )`Move conversations based on the message subject or body.`,
    categories: [RuleRecipeCategoriesEnum.MOVE],
    rule: {
      conditions: and(
        or(condition(RuleConditionTypesEnum.MESSAGE_BODY), condition(RuleConditionTypesEnum.SUBJECT)),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.MOVE)],
      events: [RuleEventTypesEnum.INBOUND],
    },
  },
  {
    id: 'move_tag',
    title: t('rule-library-recipe-move_tag-title')`Move based on tag`,
    description: t(
      'rule-library-recipe-move_tag-description',
    )`Move conversations after they are tagged with a particular tag.`,
    categories: [RuleRecipeCategoriesEnum.MOVE],
    rule: {
      conditions: and(
        condition(RuleConditionTypesEnum.ADDED_TAG_IS),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.MOVE)],
      events: [RuleEventTypesEnum.TAG, RuleEventTypesEnum.TAG_PRIVATE],
    },
  },
  {
    id: 'move_account_custom_field',
    title: t('rule-library-recipe-move_account_custom_field-title')`Move based on account custom field`,
    description: t(
      'rule-library-recipe-move_account_custom_field-description',
    )`Move conversations based on account`,
    categories: [RuleRecipeCategoriesEnum.MOVE, RuleRecipeCategoriesEnum.ACCOUNT],
    rule: {
      conditions: and(
        condition(RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_STRING),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.MOVE)],
      events: [RuleEventTypesEnum.INBOUND],
    },
  },

  /** Assign recipes */

  {
    id: 'assign_to_comment_author',
    title: t('rule-library-recipe-assign_to_comment_author-title')`Assign to comment author`,
    description: t(
      'rule-library-recipe-assign_to_comment_author-description',
    )`Assign to commenter on specific phrase.`,
    categories: [RuleRecipeCategoriesEnum.ASSIGN],
    rule: {
      events: [RuleEventTypesEnum.COMMENT],
      expressions: [
        expression('local:1', 'Comment author', [
          {transform: TransformerIdsEnum.GET, params: {property: 'comment'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'author'}},
        ]),
      ],
      conditions: and(
        condition(RuleConditionTypesEnum.COMMENT_BODY_AND_AUTHORS, {
          comparator: 'contains',
          text: 'I got this!',
        }),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.ASSIGN, {teammate: {expression: brandExpressionId('local:1')}})],
    },
  },
  {
    id: 'assign_inbox_owner',
    title: t('rule-library-recipe-assign_inbox_owner-title')`Assign to inbox owner`,
    description: t(
      'rule-library-recipe-assign_inbox_owner-description',
    )`Assign conversations to teammate in charge of the inbox.`,
    categories: [RuleRecipeCategoriesEnum.ASSIGN],
    placeholders: [
      {
        id: 'inbox_owner_custom_field',
        type: RuleRecipePlaceholderTypesEnum.CUSTOM_FIELD,
        params: {
          type: CustomFieldsEnum.TEAMMATE,
          resourceType: SettingsCustomFieldsPagesEnum.INBOXES,
          name: 'Inbox owner',
          defaultDescription: 'Teammate in charge of managing the inbox (optional)',
        },
      },
    ],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      expressions: [
        expression('local:1', 'Owner of inbox', [
          {transform: TransformerIdsEnum.GET, params: {property: 'conversation'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'inboxes'}},
          {transform: TransformerIdsEnum.FIRST},
          {
            transform: TransformerIdsEnum.GET_CUSTOM_FIELD,
            params: {custom_field: 'inbox_owner_custom_field'},
          },
        ]),
      ],
      conditions: condition(RuleConditionTypesEnum.INBOX_IN),
      actions: [action(RuleActionTypesEnum.ASSIGN, {teammate: {expression: brandExpressionId('local:1')}})],
    },
  },
  {
    id: 'assign_load_balancing',
    title: t('rule-library-recipe-assign_load_balancing-title')`Assign with load balancing`,
    description: t(
      'rule-library-recipe-assign_load_balancing-description',
    )`Assign conversations while keeping workloads balanced.`,
    categories: [RuleRecipeCategoriesEnum.ASSIGN],
    rule: {
      conditions: condition(RuleConditionTypesEnum.INBOX_IN),
      actions: [action(RuleActionTypesEnum.RANDOM_ASSIGN, {algo: 'load_balancing', teammates: []})],
      events: [RuleEventTypesEnum.INBOUND],
    },
  },
  {
    id: 'assign_round_robin',
    title: t('rule-library-recipe-assign_round_robin-title')`Assign with round-robin`,
    description: t(
      'rule-library-recipe-assign_round_robin-description',
    )`Assign conversations while alternating who gets assigned.`,
    categories: [RuleRecipeCategoriesEnum.ASSIGN],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: condition(RuleConditionTypesEnum.INBOX_IN),
      actions: [action(RuleActionTypesEnum.RANDOM_ASSIGN, {algo: 'round_robin', teammates: []})],
    },
  },
  {
    id: 'assign_sender',
    title: t('rule-library-recipe-assign_sender-title')`Assign based on sender`,
    description: t(
      'rule-library-recipe-assign_sender-description',
    )`Assign conversations sent from this person, address or number.`,
    categories: [RuleRecipeCategoriesEnum.ASSIGN],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        condition(RuleConditionTypesEnum.MESSAGE_FROM),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.ASSIGN)],
    },
  },
  {
    id: 'assign_keyword',
    title: t('rule-library-recipe-assign_keyword-title')`Assign based on keyword`,
    description: t(
      'rule-library-recipe-assign_keyword-description',
    )`Assign conversations based on the message subject or body.`,
    categories: [RuleRecipeCategoriesEnum.ASSIGN],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        or(condition(RuleConditionTypesEnum.MESSAGE_BODY), condition(RuleConditionTypesEnum.SUBJECT)),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.ASSIGN)],
    },
  },
  {
    id: 'assign_tag',
    title: t('rule-library-recipe-assign_tag-title')`Assign based on tag`,
    description: t(
      'rule-library-recipe-assign_tag-description',
    )`Assign conversations after applying a particular tag.`,
    categories: [RuleRecipeCategoriesEnum.ASSIGN],
    rule: {
      events: [RuleEventTypesEnum.TAG, RuleEventTypesEnum.TAG_PRIVATE],
      conditions: and(
        condition(RuleConditionTypesEnum.ADDED_TAG_IS),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.ASSIGN)],
    },
  },
  {
    id: 'assign_account_custom_field',
    title: t('rule-library-recipe-assign_account_custom_field-title')`Assign to account custom field`,
    description: t(
      'rule-library-recipe-assign_account_custom_field-description',
    )`Assign conversations to a custom field on the sender's account`,
    imageUrl: notifyManagerImage,
    categories: [RuleRecipeCategoriesEnum.ACCOUNT, RuleRecipeCategoriesEnum.ASSIGN],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: condition(RuleConditionTypesEnum.INBOX_IN),
      actions: [action(RuleActionTypesEnum.ASSIGN_ACCOUNT_FIELD)],
    },
  },

  /** Archive recipes */

  {
    id: 'archive_sender',
    title: t('rule-library-recipe-archive_sender-title')`Archive based on sender`,
    description: t(
      'rule-library-recipe-archive_sender-description',
    )`Archive conversations sent from this person, address or number.`,
    categories: [RuleRecipeCategoriesEnum.ARCHIVE],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        condition(RuleConditionTypesEnum.MESSAGE_FROM),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.ARCHIVE)],
    },
  },
  {
    id: 'archive_recipient',
    title: t('rule-library-recipe-archive_recipient-title')`Archive based on recipient`,
    description: t(
      'rule-library-recipe-archive_recipient-description',
    )`Archive conversations sent to this person, address or number.`,
    categories: [RuleRecipeCategoriesEnum.ARCHIVE],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        condition(RuleConditionTypesEnum.MESSAGE_ANY_RECIPIENT),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.ARCHIVE)],
    },
  },
  {
    id: 'archive_keyword',
    title: t('rule-library-recipe-archive_keyword-title')`Archive based on keyword`,
    description: t(
      'rule-library-recipe-archive_keyword-description',
    )`Archive conversations based on the message subject or body.`,
    categories: [RuleRecipeCategoriesEnum.ARCHIVE],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        or(condition(RuleConditionTypesEnum.MESSAGE_BODY), condition(RuleConditionTypesEnum.SUBJECT)),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.ARCHIVE)],
    },
  },
  {
    id: 'archive_tag',
    title: t('rule-library-recipe-archive_tag-title')`Archive based on tag`,
    description: t(
      'rule-library-recipe-archive_tag-description',
    )`Archive conversations that have been tagged with a particular tag.`,
    categories: [RuleRecipeCategoriesEnum.ARCHIVE],
    rule: {
      events: [RuleEventTypesEnum.TAG, RuleEventTypesEnum.TAG_PRIVATE],
      conditions: and(
        condition(RuleConditionTypesEnum.ADDED_TAG_IS),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.ARCHIVE)],
    },
  },

  /** Tag recipes */

  {
    id: 'tag_sender',
    title: t('rule-library-recipe-tag_sender-title')`Tag based on sender`,
    description: t(
      'rule-library-recipe-tag_sender-description',
    )`Tag conversations sent from this person, address or number.`,
    categories: [RuleRecipeCategoriesEnum.TAG],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        condition(RuleConditionTypesEnum.MESSAGE_FROM),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.TAG)],
    },
  },
  {
    id: 'tag_recipient',
    title: t('rule-library-recipe-tag_recipient-title')`Tag based on recipient`,
    description: t(
      'rule-library-recipe-tag_recipient-description',
    )`Tag conversations sent to this person, address or number.`,
    categories: [RuleRecipeCategoriesEnum.TAG],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        condition(RuleConditionTypesEnum.MESSAGE_ANY_RECIPIENT),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.TAG)],
    },
  },
  {
    id: 'tag_keyword',
    title: t('rule-library-recipe-tag_keyword-title')`Tag based on keyword`,
    description: t(
      'rule-library-recipe-tag_keyword-description',
    )`Tag conversations based on the message subject or body.`,
    categories: [RuleRecipeCategoriesEnum.TAG],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        or(condition(RuleConditionTypesEnum.MESSAGE_BODY), condition(RuleConditionTypesEnum.SUBJECT)),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.TAG)],
    },
  },
  {
    id: 'tag_untag',
    title: t('rule-library-recipe-tag_untag-title')`Tag and untag`,
    description: t(
      'rule-library-recipe-tag_untag-description',
    )`Remove an existing tag when a new tag is applied.`,
    categories: [RuleRecipeCategoriesEnum.TAG],
    rule: {
      events: [RuleEventTypesEnum.TAG, RuleEventTypesEnum.TAG_PRIVATE],
      conditions: and(
        condition(RuleConditionTypesEnum.ADDED_TAG_IS),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.UNTAG)],
    },
  },

  /** Notify recipes */

  {
    id: 'notify_manager',
    title: t('rule-library-recipe-notify_manager-title')`Notify manager`,
    description: t(
      'rule-library-recipe-notify_manager-description',
    )`Notify assignee’s manager in long conversations.`,
    imageUrl: notifyManagerImage,
    categories: [RuleRecipeCategoriesEnum.SMART_RULES, RuleRecipeCategoriesEnum.NOTIFY],
    placeholders: [
      {
        id: 'assignee_manager_custom_field',
        type: RuleRecipePlaceholderTypesEnum.CUSTOM_FIELD,
        params: {
          type: CustomFieldsEnum.TEAMMATE,
          resourceType: SettingsCustomFieldsPagesEnum.TEAMMATES,
          name: 'Manager',
          defaultDescription: 'Manager of the teammate',
        },
      },
    ],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      expressions: [
        expression('local:1', 'Number of messages', [
          {transform: TransformerIdsEnum.GET, params: {property: 'conversation'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'num_messages'}},
        ]),
        expression('local:2', 'Manager of assignee', [
          {transform: TransformerIdsEnum.GET, params: {property: 'conversation'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'assignee'}},
          {
            transform: TransformerIdsEnum.GET_CUSTOM_FIELD,
            params: {custom_field: 'assignee_manager_custom_field'},
          },
        ]),
      ],
      conditions: and(
        condition(RuleConditionTypesEnum.INBOX_IN),
        condition(RuleConditionTypesEnum.COMPARE_EXPRESSION_NUMBER, {
          expression: brandExpressionId('local:1'),
          comparator: 'greater_than',
          value: 20,
        }),
      ),
      actions: [
        action(RuleActionTypesEnum.NOTIFY_TEAMMATES, {
          teammates: [{expression: brandExpressionId('local:2')}],
        }),
      ],
    },
  },
  {
    id: 'notify_sender',
    title: t('rule-library-recipe-notify_sender-title')`Notify based on sender`,
    description: t(
      'rule-library-recipe-notify_sender-description',
    )`Alert teammates to messages sent from a person or address.`,
    categories: [RuleRecipeCategoriesEnum.NOTIFY],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        condition(RuleConditionTypesEnum.MESSAGE_FROM),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.NOTIFY_TEAMMATES)],
    },
  },
  {
    id: 'notify_keyword',
    title: t('rule-library-recipe-notify_keyword-title')`Notify based on keyword`,
    description: t(
      'rule-library-recipe-notify_keyword-description',
    )`Alert teammates based on the message subject or body.`,
    categories: [RuleRecipeCategoriesEnum.NOTIFY],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        or(condition(RuleConditionTypesEnum.MESSAGE_BODY), condition(RuleConditionTypesEnum.SUBJECT)),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.NOTIFY_TEAMMATES)],
    },
  },
  {
    id: 'notify_comment',
    title: t('rule-library-recipe-notify_comment-title')`Notify when comment made`,
    description: t(
      'rule-library-recipe-notify_comment-description',
    )`Alert teammates based on a comment body.`,
    categories: [RuleRecipeCategoriesEnum.NOTIFY],
    rule: {
      events: [RuleEventTypesEnum.COMMENT],
      conditions: and(
        condition(RuleConditionTypesEnum.COMMENT_BODY_AND_AUTHORS),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.NOTIFY_TEAMMATES)],
    },
  },
  {
    id: 'notify_tag',
    title: t('rule-library-recipe-notify_tag-title')`Notify when tag applied`,
    description: t(
      'rule-library-recipe-notify_tag-description',
    )`Notify teammates after applying a particular tag.`,
    categories: [RuleRecipeCategoriesEnum.NOTIFY],
    rule: {
      events: [RuleEventTypesEnum.TAG, RuleEventTypesEnum.TAG_PRIVATE],
      conditions: and(
        condition(RuleConditionTypesEnum.ADDED_TAG_IS),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.NOTIFY_TEAMMATES)],
    },
  },
  {
    id: 'notify_assignee_unreplied',
    title: t('rule-library-recipe-notify_assignee_unreplied-title')`Notify when unreplied`,
    description: t(
      'rule-library-recipe-notify_assignee_unreplied-description',
    )`Notify an assignee about unreplied conversations.`,
    categories: [RuleRecipeCategoriesEnum.NOTIFY],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        condition(RuleConditionTypesEnum.UNREPLIED_AFTER),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.NOTIFY_ASSIGNEE)],
    },
  },
  {
    id: 'notify_outbound_unreplied',
    title: t('rule-library-recipe-notify_outbound_unreplied-title')`Notify when waiting`,
    description: t(
      'rule-library-recipe-notify_outbound_unreplied-description',
    )`Notify teammates when waiting for a customer response.`,
    categories: [RuleRecipeCategoriesEnum.NOTIFY],
    rule: {
      events: [RuleEventTypesEnum.OUTBOUND, RuleEventTypesEnum.OUT_REPLY],
      conditions: and(
        condition(RuleConditionTypesEnum.UNREPLIED_AFTER),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.NOTIFY_TEAMMATES)],
    },
  },
  {
    id: 'notify_account_custom_field',
    title: t('rule-library-recipe-notify_account_custom_field-title')`Notify account custom field`,
    description: t(
      'rule-library-recipe-notify_account_custom_field-description',
    )`Notify based on custom fields on the sender's account`,
    categories: [RuleRecipeCategoriesEnum.NOTIFY, RuleRecipeCategoriesEnum.ACCOUNT],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: condition(RuleConditionTypesEnum.INBOX_IN),
      actions: [action(RuleActionTypesEnum.NOTIFY_ACCOUNT_FIELD)],
    },
  },

  /** Reply recipes */

  {
    id: 'reply_business_hours',
    title: t('rule-library-recipe-reply_business_hours-title')`Reply outside of business hours`,
    description: t(
      'rule-library-recipe-reply_business_hours-description',
    )`Send an auto-reply outside of business hours.`,
    categories: [RuleRecipeCategoriesEnum.REPLY],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        condition(RuleConditionTypesEnum.OUTSIDE_BUSINESS_HOURS),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.REPLY, {when: 'once'})],
    },
  },
  {
    id: 'reply_time_passed',
    title: t('rule-library-recipe-reply_time_passed-title')`Reply when unreplied`,
    description: t(
      'rule-library-recipe-reply_time_passed-description',
    )`Send an auto-reply acknowledging for any delay in response.`,
    categories: [RuleRecipeCategoriesEnum.REPLY],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      conditions: and(
        condition(RuleConditionTypesEnum.UNREPLIED_AFTER),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.REPLY, {when: 'once'})],
    },
  },
  {
    id: 'reply_tag',
    title: t('rule-library-recipe-reply_tag-title')`Reply when tag applied`,
    description: t('rule-library-recipe-reply_tag-description')`Send an auto-reply when a tag is applied.`,
    categories: [RuleRecipeCategoriesEnum.REPLY],
    rule: {
      events: [RuleEventTypesEnum.TAG, RuleEventTypesEnum.TAG_PRIVATE],
      conditions: and(
        condition(RuleConditionTypesEnum.ADDED_TAG_IS),
        condition(RuleConditionTypesEnum.INBOX_IN),
      ),
      actions: [action(RuleActionTypesEnum.REPLY, {when: 'once'})],
    },
  },

  /** Comment recipes */

  {
    id: 'comment_urls',
    title: t('rule-library-recipe-comment_urls-title')`Extract URLs from message`,
    description: t('rule-library-recipe-comment_urls-description')`Add all URLs in a message as a comment.`,
    categories: [RuleRecipeCategoriesEnum.COMMENT],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      expressions: [
        expression('local:1', 'Extracted URLs', [
          {transform: TransformerIdsEnum.GET, params: {property: 'message'}},
          {transform: TransformerIdsEnum.GET_MESSAGE_BODY},
          {transform: TransformerIdsEnum.EXTRACT_URLS, params: {occurrences: 'all'}},
        ]),
      ],
      conditions: and(
        condition(RuleConditionTypesEnum.INBOX_IN),
        condition(RuleConditionTypesEnum.COMPARE_EXPRESSION_LIST, {
          expression: brandExpressionId('local:1'),
          comparator: 'is_not_empty',
        }),
      ),
      actions: [
        action(RuleActionTypesEnum.ADD_COMMENT, {
          body: {
            template: [
              'Here are all the URLs in this message:\n',
              {expression: brandExpressionId('local:1')},
            ],
          },
        }),
      ],
    },
  },
  {
    id: 'comment_ids',
    title: t('rule-library-recipe-comment_ids-title')`Extract IDs from message`,
    description: t(
      'rule-library-recipe-comment_ids-description',
    )`Add order, booking or customer IDs as a comment.`,
    categories: [RuleRecipeCategoriesEnum.COMMENT],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      expressions: [
        expression('local:1', 'Extracted IDs', [
          {transform: TransformerIdsEnum.GET, params: {property: 'message'}},
          {transform: TransformerIdsEnum.GET_MESSAGE_BODY},
          {
            transform: TransformerIdsEnum.EXTRACT_TEXT,
            params: {
              occurrences: 'all',
              pattern: {
                template: [
                  {char: RegexCharacterClassesEnum.ALPHA, repeat: 2},
                  '-',
                  {char: RegexCharacterClassesEnum.DIGIT, repeat: 5},
                  '-',
                  {char: RegexCharacterClassesEnum.DIGIT, repeat: 2},
                ],
              },
            },
          },
        ]),
      ],
      conditions: and(
        condition(RuleConditionTypesEnum.INBOX_IN),
        condition(RuleConditionTypesEnum.COMPARE_EXPRESSION_LIST, {
          expression: brandExpressionId('local:1'),
          comparator: 'is_not_empty',
        }),
      ),
      actions: [
        action(RuleActionTypesEnum.ADD_COMMENT, {
          body: {
            template: [
              'Here are all the order/customer/booking IDs in this message:\n',
              {expression: brandExpressionId('local:1')},
            ],
          },
        }),
      ],
    },
  },
  {
    id: 'comment_contact_fields',
    title: t('rule-library-recipe-comment_contact_fields-title')`Contact fields in comment`,
    description: t(
      'rule-library-recipe-comment_contact_fields-description',
    )`Add contact fields in a comment.`,
    categories: [RuleRecipeCategoriesEnum.COMMENT],
    rule: {
      events: [RuleEventTypesEnum.INBOUND],
      expressions: [
        expression('local:1', 'Contact Name', [
          {transform: TransformerIdsEnum.GET, params: {property: 'conversation'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'contact'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'name'}},
        ]),
        expression('local:2', 'Contact Email', [
          {transform: TransformerIdsEnum.GET, params: {property: 'conversation'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'contact'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'email'}},
        ]),
        expression('local:3', 'Contact Phone Number', [
          {transform: TransformerIdsEnum.GET, params: {property: 'conversation'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'contact'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'phone'}},
        ]),
        expression('local:4', 'Contact Twitter', [
          {transform: TransformerIdsEnum.GET, params: {property: 'conversation'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'contact'}},
          {transform: TransformerIdsEnum.GET, params: {property: 'twitter'}},
        ]),
      ],
      conditions: and(
        condition(RuleConditionTypesEnum.INBOX_IN),
        condition(RuleConditionTypesEnum.COMPARE_EXPRESSION_STRING, {
          expression: brandExpressionId('local:1'),
          comparator: 'is_defined',
        }),
      ),
      actions: [
        action(RuleActionTypesEnum.ADD_COMMENT, {
          body: {
            template: [
              'Contact Details:\n- Name: ',
              {expression: brandExpressionId('local:1')},
              '\n- Email: ',
              {expression: brandExpressionId('local:2')},
              '\n- Phone: ',
              {expression: brandExpressionId('local:3')},
              '\n- Twitter: ',
              {expression: brandExpressionId('local:4')},
            ],
          },
        }),
      ],
    },
  },
];

export interface RuleRecipe {
  id: string;
  title: MessageDescriptor;
  description: MessageDescriptor;
  categories: ReadonlyArray<RuleRecipeCategoriesEnum>;
  requiredFeatures: ReadonlyArray<CompanyFeaturesEnum>;
  placeholders?: ReadonlyArray<RuleRecipePlaceholder>;
  imageUrl?: string;
  rule: {
    conditions: RuleCompoundConditions;
    actions: ReadonlyArray<RuleAction>;
    events: ReadonlyArray<RuleEventTypesEnum>;
    expressions?: ReadonlyArray<PreExpression>;
  };
}

export function useRuleLibraryRecipes(integrations: RuleIntegration): ReadonlyArray<RuleRecipe> {
  return useMemo(() => Object.freeze(processRecipes(RECIPES, integrations)), [integrations]);
}
